/**
 * Platform Admin tenant list (FR-N).
 */

import { useQuery } from "@tanstack/react-query";

import { getPlatformTenants } from "@starz/shared/api/sdk.gen";

export function PlatformTenants() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["platform-tenants"],
    queryFn: async () => {
      const result = await getPlatformTenants();
      if ("error" in result) throw new Error(result.error.error.message);
      return result.data;
    },
  });

  return (
    <div className="mx-auto max-w-content px-12 py-16">
      <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-starz-gold">
        Platform admin
      </p>
      <h1 className="mb-3 font-serif text-4xl text-starz-navy">Tenants</h1>
      <div className="mb-8 h-0.5 w-14 bg-starz-gold" aria-hidden="true" />

      {isLoading && <p className="text-warm-gray">Loading…</p>}
      {isError && <p className="text-starz-brick">Failed to load tenants. Try again.</p>}
      {data && (
        <div className="overflow-hidden rounded-xl border border-starz-gold/15 bg-white">
          <table className="min-w-full divide-y divide-starz-gold/10">
            <thead className="bg-starz-cream">
              <tr>
                <Th>Tenant</Th>
                <Th>Subdomain</Th>
                <Th>Status</Th>
                <Th>Created</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-starz-gold/10">
              {data.map((t) => (
                <tr key={t.tenant_id}>
                  <Td>
                    <div className="font-medium text-starz-navy">{t.display_name}</div>
                    <div className="text-xs text-warm-gray">{t.tenant_id}</div>
                  </Td>
                  <Td>{t.subdomain}</Td>
                  <Td>
                    <StatusPill status={t.status} />
                  </Td>
                  <Td className="text-sm text-warm-gray">
                    {new Date(t.created_at).toLocaleDateString()}
                  </Td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-warm-gray">
                    No tenants configured.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-warm-gray"
    >
      {children}
    </th>
  );
}

function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-6 py-4 ${className}`}>{children}</td>;
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === "active"
      ? "bg-starz-gold/10 text-starz-gold"
      : status === "suspended"
        ? "bg-starz-brick/10 text-starz-brick"
        : "bg-warm-gray/10 text-warm-gray";
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider ${tone}`}
    >
      {status}
    </span>
  );
}
